import { useDebounceFn } from "ahooks";
import { getValue, setValue } from "./utils";
import React, { useRef, useState } from "react";

import type { TypeProfileInfo } from "./interface";
import type {
  Callbacks,
  FieldData,
} from "antd/node_modules/rc-field-form/lib/interface";

const FIELDS_RE_AUDIT = Object.fromEntries(
  [
    // 这些路径字段若发生改变，需要重新发起审核
    ["NMLS"],
    ["LastName"],
    ["LandingPageSetUp", "States"],
    ["LandingPageSetUp", "StateCapabilities"],
  ].map((v) => [v.join(), true]),
);

export const STATE_CAPABILITIES_FIELD =
  "LandingPageSetUp,StateCapabilities";

/**
 * @name useAsyncUpdate 仅关注表单数据更新
 * @description 新的需求去除了草稿功能，但是又要达到用户无感知“草稿”功能，简单说就是实现一个状态隔离，不符合规范的状态，并不会提交给服务器
 */
export function useAsyncUpdate<
  T extends TypeProfileInfo.DTO = TypeProfileInfo.DTO,
>(
  updateFn: (
    data: T,
    signal: AbortSignal,
    audit: boolean,
    changedFields: string[],
  ) => Promise<void>,
) {
  const abort = useRef<AbortController>();

  const updated = useRef(false);

  const target = useRef<Record<string, FieldData>>({}); // 当前操作的字段
  const cacheServerData = useRef<T>(); // 存储服务器无污染data

  const [sync, setSync] = useState(false);

  /**
   * @name onFieldsChange 字段变更时触发
   */
  const onFieldsChange: Callbacks["onFieldsChange"] = async (fields) => {
    fields.forEach((v) => {
      target.current[v.name.join()] = v;
      if (v.name.join() === STATE_CAPABILITIES_FIELD) {
        target.current["LandingPageSetUp,States"] = {
          name: ["LandingPageSetUp", "States"],
          value: Array.isArray(v.value)
            ? v.value.map((item) => item?.state).filter(Boolean)
            : [],
          errors: v.errors,
        };
      }
    });
    updated.current = true;
  };

  /**
   * @name executionQueue 执行字段更新任务队列
   */
  function executionQueue(updates: FieldData[]) {
    return new Promise((resolve) => {
      requestIdleCallback(function queues(deadline) {
        while (deadline.timeRemaining() && updates.length) {
          const v = updates.shift();
          v && setValue(cacheServerData.current, v);
        }
        updates.length ? requestIdleCallback(queues) : resolve(true);
      });
    });
  }

  /**
   * @name setServerData 这里存储服务器的原始数据，不被表单操作而污染
   */
  function setServerData(form: T) {
    cacheServerData.current = form;
  }

  const { run: onMouseMove } = useDebounceFn(
    async () => {
      if (updated.current) {
        updated.current = false;
        const changes = Object.values(target.current);
        if (!changes?.length) return;
        target.current = {};
        let reAudit = false;
        const updates = changes.filter((v) => {
          if (!reAudit && FIELDS_RE_AUDIT[v.name.join()]) {
            reAudit = true;
          }
          // diff 值，有变化才提交到 update 队列
          if (v.errors?.length) return false;

          const cachedValue = getValue(cacheServerData.current, v.name);
          return v.name.join() === STATE_CAPABILITIES_FIELD
            ? JSON.stringify(v.value) !== JSON.stringify(cachedValue)
            : String(v.value) !== String(cachedValue);
        });
        if (updates?.length && cacheServerData.current) {
          try {
            setSync(true);
            abort.current?.abort?.();
            abort.current = new AbortController();
            await executionQueue(updates);
            await updateFn(
              cacheServerData.current,
              abort.current.signal,
              reAudit,
              updates.map((field) => field.name.join()),
            );
          } catch (e) {
          } finally {
            setSync(false);
          }
        }
      }
    },
    { wait: 300, maxWait: 800 },
  );

  return {
    /**
     * @param sync 同步状态
     */
    sync,
    /**
     * @name setServerData 设置原始数据
     * @description 存储API的原始数据，不被表单操作而污染
     */
    setServerData,
    /**
     * @param event 实现按需监听更新字段 所需要注册的一系列事件方法
     */
    event: { onMouseMove, onFieldsChange },
  };
}

/**
 * @name useDefocusEvent 用户脱离指定区域，则触发“失焦事件”
 * @description 有些自定义组件、交互没有 失焦 事件，所以需要通过此 Hook 来实现触发（手动挡）🤡
 */
// export function useDefocusEvent<T extends HTMLElement>() {
//   const ref = useRef<T>();

//   const [click, setClick] = useState(false);

//   function onMouseLeave() {
//     setClick(false);
//     setTimeout(() => {
//       ref.current.blur();
//       ref.current.focus();
//     });
//   }

//   function onClick() {
//     setClick(true);
//   }

//   return {
//     ref,
//     onClick,
//     tabIndex: 0,
//     onMouseLeave: click ? onMouseLeave : undefined, // 触发聚焦、失焦
//   };
// }
